import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import Icon from "@mui/material/Icon";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import { FormControl, FormHelperText, InputLabel, Select } from "@mui/material";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import ConfiguratorRoot from "../../examples/Configurator/ConfiguratorRoot";
import MDBox from "../../components/MDBox";
import MDTypography from "../../components/MDTypography";
import MDInput from "../../components/MDInput";
import MDButton from "../../components/MDButton";
import useApi from "../../components/Utils/api";

// Supported device types — same list as the terminal form
const DEVICE_TYPES = ["Y1000K"];
const emptyDevice = { deviceName: "", deviceType: DEVICE_TYPES[0], serialNumber: "", status: "ACTIVE" };

function NewDevice({ openConfigurator, setOpenConfigurator, device, onSaved }) {
  const { t } = useTranslation();
  const api = useApi();
  const panelRef = useRef(null);
  const [form, setForm] = useState(emptyDevice);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const isUpdate = Object.keys(device).length > 0;

  const closeConfigurator = () => {
    setErrors({});
    setOpenConfigurator(false);
  };

  useEffect(() => {
    if (!openConfigurator) return;
    const handleClickOutside = (event) => {
      if (event.target.closest(".MuiPopover-root, .MuiModal-root, .MuiMenu-root")) {
        return;
      }
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        closeConfigurator();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openConfigurator]);

  useEffect(() => {
    setErrors({});
    setForm(isUpdate
      ? {
        deviceName: device.deviceName || "",
        deviceType: device.deviceType || "",
        serialNumber: device.serialNumber || "",
        status: device.status || "ACTIVE",
      }
      : emptyDevice);
  }, [openConfigurator, device]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!form.deviceName.trim()) next.deviceName = t("devicePage.nameRequired");
    if (!form.deviceType.trim()) next.deviceType = t("devicePage.typeRequired");
    if (!form.serialNumber.trim()) {
      next.serialNumber = t("devicePage.serialRequired");
    } else if (!/^[A-Za-z0-9-]+$/.test(form.serialNumber.trim())) {
      next.serialNumber = t("devicePage.serialFormat");
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  // Validation failures come back as { errors: [...] }; service errors as { message: "..." }
  const getErrorMessage = (error) => {
    const data = error.response?.data;
    if (Array.isArray(data?.errors) && data.errors.length > 0) return data.errors.join("\n");
    return data?.message || t("something_went_wrong");
  };

  const submitHandler = () => {
    if (!validate()) return;
    const payload = {
      ...form,
      deviceName: form.deviceName.trim(),
      deviceType: form.deviceType.trim(),
      serialNumber: form.serialNumber.trim(),
      ...(isUpdate && { id: device.id }),
    };
    setSaving(true);
    api.post(isUpdate ? "device/update" : "device/create", payload)
      .then(() => {
        toast.success(isUpdate ? t("devicePage.updated") : t("devicePage.created"));
        setOpenConfigurator(false);
        onSaved();
      })
      .catch((error) => {
        if (error.response?.status === 403) {
          toast.error(t("you_do_not_have_permission_to_perform_this_action"), { id: "PermissionError" });
          return;
        }
        toast.error(getErrorMessage(error));
      })
      .finally(() => setSaving(false));
  };

  return (
    <ConfiguratorRoot
      variant="permanent"
      ownerState={{ openConfigurator }}
      configuratorwidth={500}
      onClose={closeConfigurator}
      PaperProps={{ ref: panelRef }}
    >
      <MDBox display="flex" justifyContent="space-between" alignItems="baseline" pt={4} pb={0.5} px={3}>
        <MDBox>
          <MDTypography variant="h5">{isUpdate ? t("update") : t("new")} {t("devicePage.device")}</MDTypography>
          {isUpdate && device.mapped && (
            <MDTypography variant="button" color="text">
              {t("devicePage.mappedTo", { mid: device.mid, tid: device.tid })}
            </MDTypography>
          )}
        </MDBox>
        <Icon
          sx={({ typography: { size }, palette: { dark } }) => ({
            fontSize: `${size.lg} !important`,
            color: dark.main,
            stroke: "currentColor",
            strokeWidth: "2px",
            cursor: "pointer",
            transform: "translateY(5px)",
          })}
          onClick={closeConfigurator}
        >
          close
        </Icon>
      </MDBox>
      <Divider />
      <Grid container spacing={2} px={3}>
        <Grid item xs={12}>
          <MDInput
            label={t("deviceTableColumns.name")} name="deviceName" value={form.deviceName}
            onChange={handleChange} fullWidth required inputProps={{ maxLength: 100 }}
            error={!!errors.deviceName} helperText={errors.deviceName || ""}
          />
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth required error={!!errors.deviceType}>
            <InputLabel id="device-type-label">{t("deviceTableColumns.type")}</InputLabel>
            <Select
              labelId="device-type-label" name="deviceType" value={form.deviceType}
              onChange={handleChange} label={t("deviceTableColumns.type")} sx={{ height: "44px" }}
            >
              {/* Keep a legacy type selectable so existing devices still show their value */}
              {[...new Set([...DEVICE_TYPES, form.deviceType].filter(Boolean))].map((type) => (
                <MenuItem key={type} value={type}>{type}</MenuItem>
              ))}
            </Select>
            {errors.deviceType && <FormHelperText>{errors.deviceType}</FormHelperText>}
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label={t("deviceTableColumns.serialNumber")} name="serialNumber" value={form.serialNumber}
            onChange={handleChange} fullWidth required inputProps={{ maxLength: 50 }}
            disabled={isUpdate && device.mapped}
            error={!!errors.serialNumber}
            helperText={errors.serialNumber || (isUpdate && device.mapped ? t("devicePage.serialLocked") : "")}
          />
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel id="device-status-label">{t("deviceTableColumns.status")}</InputLabel>
            <Select
              labelId="device-status-label" name="status" value={form.status}
              onChange={handleChange} label={t("deviceTableColumns.status")} sx={{ height: "44px" }}
            >
              <MenuItem value="ACTIVE">{t("active")}</MenuItem>
              <MenuItem value="INACTIVE">{t("inactive")}</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <MDButton onClick={submitHandler} variant="gradient" color="info" disabled={saving} style={{ float: "right" }}>
            {isUpdate ? t("update") : t("add")}
          </MDButton>
        </Grid>
      </Grid>
    </ConfiguratorRoot>
  );
}

NewDevice.defaultProps = {
  openConfigurator: false,
  device: {},
};

NewDevice.propTypes = {
  openConfigurator: PropTypes.bool,
  setOpenConfigurator: PropTypes.func.isRequired,
  device: PropTypes.object,
  onSaved: PropTypes.func.isRequired,
};

export default NewDevice;
